import { useState, useRef } from 'react';
import './VoiceTest.css';

const VoiceTest = ({ apiBaseUrl = '' }) => {
  const [voiceFile, setVoiceFile] = useState(null);
  const [result, setResult] = useState(null);
  const [recording, setRecording] = useState(false);
  const [recordingUrl, setRecordingUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedVoice, setSelectedVoice] = useState('alloy');
  
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  const VOICES = ['alloy', 'echo', 'fable', 'onyx', 'nova', 'shimmer'];

  const startRecording = async () => {
    if (recording) return;
    
    if (!navigator.mediaDevices?.getUserMedia) {
      setError('Microphone access is not supported in this browser.');
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      chunksRef.current = [];

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      recorder.onstop = () => {
        const blobType = recorder.mimeType || 'audio/webm';
        const blob = new Blob(chunksRef.current, { type: blobType });
        const file = new File([blob], `voice-test-${Date.now()}.webm`, { type: blobType });
        stream.getTracks().forEach((track) => track.stop());

        if (recordingUrl) {
          URL.revokeObjectURL(recordingUrl);
        }
        
        setRecordingUrl(URL.createObjectURL(blob));
        setVoiceFile(file);
        setResult(null);
        setRecording(false);
        setError('');
      };

      mediaRecorderRef.current = recorder;
      recorder.start();
      setRecording(true);
      setError('');
    } catch (err) {
      setError(err.message || 'Microphone access failed.');
    }
  };

  const stopRecording = () => {
    const recorder = mediaRecorderRef.current;
    if (!recorder || recorder.state !== 'recording') return;
    recorder.stop();
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      setVoiceFile(file);
      setResult(null);
      setError('');
      
      // Clean up old recording URL
      if (recordingUrl) {
        URL.revokeObjectURL(recordingUrl);
        setRecordingUrl('');
      }
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!voiceFile) {
      setError('Please record or select an audio file first.');
      return;
    }

    setLoading(true);
    setError('');
    setResult(null);

    try {
      const formData = new FormData();
      formData.append('audio', voiceFile);
      formData.append('voice', selectedVoice);

      const response = await fetch(`${apiBaseUrl}/test/transcribe-audio`, {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Transcription failed');
      }

      setResult(data);
    } catch (err) {
      setError(err.message || 'An error occurred during transcription.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="voice-test-container">
      <div className="voice-test-header">
        <h2>🎙️ Voice Test</h2>
        <p>Test AI transcription and text-to-speech</p>
      </div>

      <form onSubmit={handleSubmit} className="voice-test-form">
        {/* Recording Controls */}
        <div className="control-group">
          <label className="control-label">Record Audio</label>
          <div className="recording-controls">
            {!recording ? (
              <button
                type="button"
                onClick={startRecording}
                className="btn btn-record"
                disabled={loading}
              >
                🎤 Start Recording
              </button>
            ) : (
              <button
                type="button"
                onClick={stopRecording}
                className="btn btn-stop"
              >
                ⏹️ Stop Recording
              </button>
            )}
            {recording && <span className="recording-indicator">● Recording...</span>}
          </div>
        </div>

        {/* File Upload */}
        <div className="control-group">
          <label className="control-label">Or Upload Audio File</label>
          <input
            type="file"
            accept="audio/*"
            onChange={handleFileChange}
            className="file-input"
            disabled={loading || recording}
          />
        </div>

        {/* Preview Recorded Audio */}
        {recordingUrl && (
          <div className="control-group">
            <label className="control-label">Preview Recording</label>
            <audio controls src={recordingUrl} className="audio-player" />
          </div>
        )}

        {/* Voice Selection */}
        <div className="control-group">
          <label className="control-label">AI Voice</label>
          <select
            value={selectedVoice}
            onChange={(e) => setSelectedVoice(e.target.value)}
            className="voice-select"
            disabled={loading}
          >
            {VOICES.map((voice) => (
              <option key={voice} value={voice}>
                {voice.charAt(0).toUpperCase() + voice.slice(1)}
              </option>
            ))}
          </select>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          className="btn btn-primary"
          disabled={!voiceFile || loading || recording}
        >
          {loading ? '⏳ Processing...' : '🚀 Transcribe & Generate Reply'}
        </button>
      </form>

      {/* Error Message */}
      {error && (
        <div className="alert alert-error">
          <strong>Error:</strong> {error}
        </div>
      )}

      {/* Results */}
      {result && (
        <div className="voice-test-results">
          <div className="result-section">
            <h3>📝 Transcription</h3>
            <div className="result-box">
              {result.transcription || 'No transcription available'}
            </div>
          </div>

          <div className="result-section">
            <h3>🤖 AI Reply</h3>
            <div className="result-box">
              {result.ai_reply || 'No reply generated'}
            </div>
          </div>

          {result.audio_url && (
            <div className="result-section">
              <h3>🔊 AI Voice Response</h3>
              <audio controls src={result.audio_url} className="audio-player" />
              <a
                href={result.audio_url}
                download="ai-response.mp3"
                className="btn btn-download"
              >
                ⬇️ Download Audio
              </a>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default VoiceTest;
